import React, { memo, useCallback, useEffect, useState } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, MenuItem,
} from "@mui/material";
import IpdTextField from "../../../../utils/IpdTextField";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import FormField from "../FormField";
import ConfigurableField from "../ConfigurableField";
import { SOURCE_TYPES } from "../../constants/registrationConstants";
import { CONFIGURED_SECTION_IDS } from "../../utils/sectionConfiguration";
import { useSectionConfig } from "../../context/SectionConfigContext";
import {
  addReferralDoctorToMaster,
  fetchReferralDoctors,
} from "../../api/registrationService";

const S = CONFIGURED_SECTION_IDS;

function PatientSourceSection({ patient, updateSource, refDoctors, setRefDoctors, errors = {} }) {
  const { isFieldActive, isFieldMandatory, config } = useSectionConfig();
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [addingDoctor, setAddingDoctor] = useState(false);

  const showSourceValue = !config
    || (isFieldActive(S.source, "sourceTypeValue") && Boolean(patient.source.sourceType));

  const sourceValueRequired = config ? isFieldMandatory(S.source, "sourceTypeValue") : false;

  const loadReferralDoctors = useCallback(async () => {
    setLoadingDoctors(true);
    try {
      const doctors = await fetchReferralDoctors();
      setRefDoctors(doctors);
      return doctors;
    } catch (err) {
      toast.error(err.message || "Failed to load referral doctors");
      return [];
    } finally {
      setLoadingDoctors(false);
    }
  }, [setRefDoctors]);

  useEffect(() => {
    if (patient.source.sourceType !== "EXTERNAL_DOCTOR") return undefined;
    loadReferralDoctors();
    return undefined;
  }, [patient.source.sourceType, loadReferralDoctors]);

  const addDoctor = async () => {
    if (!newName.trim() || !/^\d{7,}$/.test(newPhone)) {
      toast.error("Doctor name and valid phone are required");
      return;
    }

    setAddingDoctor(true);
    try {
      const created = await addReferralDoctorToMaster({
        name: newName,
        phoneNo: newPhone,
      });
      const doctors = await loadReferralDoctors();
      const selectedId = created.id
        || doctors.find((doctor) => doctor.phone === newPhone)?.id
        || "";
      if (selectedId) {
        updateSource("referralDoctorId", selectedId);
      }
      setNewName("");
      setNewPhone("");
      setAddOpen(false);
      toast.success(created.message || "Referral doctor added to master");
    } catch (err) {
      toast.error(err.message || "Failed to add referral doctor");
    } finally {
      setAddingDoctor(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <ConfigurableField sectionId={S.source} fieldName="sourceType" xs={12} md={4}>
        {({ required }) => (
          <FormField label="Source" required={required} error={errors.sourceType}>
            <IpdTextField select fullWidth size="small" value={patient.source.sourceType} onChange={(e) => updateSource("sourceType", e.target.value)} displayEmpty error={Boolean(errors.sourceType)}>
              <MenuItem value="">Select source</MenuItem>
              {SOURCE_TYPES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </IpdTextField>
          </FormField>
        )}
      </ConfigurableField>

      {showSourceValue && patient.source.sourceType === "HOSPITAL" && (
        <Grid item xs={12} md={8}>
          <FormField label="Hospital Name" required={sourceValueRequired} error={errors.sourceTypeValue}>
            <IpdTextField fullWidth size="small" value={patient.source.hospitalName} onChange={(e) => updateSource("hospitalName", e.target.value)} error={Boolean(errors.sourceTypeValue)} />
          </FormField>
        </Grid>
      )}
      {showSourceValue && patient.source.sourceType === "MARKETING_PERSON" && (
        <Grid item xs={12} md={8}>
          <FormField label="Marketing Person Name" required={sourceValueRequired} error={errors.sourceTypeValue}>
            <IpdTextField fullWidth size="small" value={patient.source.marketingName} onChange={(e) => updateSource("marketingName", e.target.value)} error={Boolean(errors.sourceTypeValue)} />
          </FormField>
        </Grid>
      )}
      {showSourceValue && patient.source.sourceType === "EMPLOYEE" && (
        <Grid item xs={12} md={8}>
          <FormField label="Employee Name" required={sourceValueRequired} error={errors.sourceTypeValue}>
            <IpdTextField fullWidth size="small" value={patient.source.employeeName} onChange={(e) => updateSource("employeeName", e.target.value)} error={Boolean(errors.sourceTypeValue)} />
          </FormField>
        </Grid>
      )}
      {showSourceValue && patient.source.sourceType === "EXTERNAL_DOCTOR" && (
        <Grid item xs={12} md={8}>
          <FormField label="Referral Doctor" required={sourceValueRequired} error={errors.sourceTypeValue}>
            <Box display="flex" gap={1}>
              <IpdTextField select fullWidth size="small" value={patient.source.referralDoctorId} onChange={(e) => updateSource("referralDoctorId", e.target.value)} disabled={loadingDoctors} displayEmpty error={Boolean(errors.sourceTypeValue)}>
                <MenuItem value="">
                  {loadingDoctors ? "Loading doctors..." : "Select doctor"}
                </MenuItem>
                {refDoctors.map((d) => (
                  <MenuItem key={d.id} value={d.id}>{d.name} · {d.phone}</MenuItem>
                ))}
              </IpdTextField>
              <IconButton aria-label="Add referral doctor" onClick={() => setAddOpen(true)}><AddIcon /></IconButton>
            </Box>
          </FormField>
        </Grid>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Referral Doctor to Master</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormField label="Doctor Name">
                <IpdTextField fullWidth size="small" placeholder="Dr. ..." value={newName} onChange={(e) => setNewName(e.target.value)} />
              </FormField>
            </Grid>
            <Grid item xs={12}>
              <FormField label="Phone">
                <IpdTextField fullWidth size="small" placeholder="10-digit phone" value={newPhone} onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, ""))} />
              </FormField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={addingDoctor}>Cancel</Button>
          <Button variant="contained" onClick={addDoctor} disabled={addingDoctor}>
            {addingDoctor ? "Adding..." : "Add to Master"}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

export default memo(PatientSourceSection);
